import { Drawer, Switch } from "antd";
import React, { useEffect, useState } from "react";
import Input from "../../atoms/Input/Input";
import ErrorText from "../../atoms/Error/errorText";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { getEmployeesAPI } from "../../../Redux/Apis/employeeApi";
import { isCeoOrCmoRoleUser } from "../../../app/utils/common/Functions";
import { PROJECT_TYPE } from "../../../app/utils/constants/option";
import {
  createProjectAPI,
  updateProjectAPI,
} from "../../../Redux/Apis/projectApi";

const ProjectDrawer = ({
  project,
  isEditOpen,
  toggleDetailDrawer,
  isCreate = true,
}) => {
  const [showError, setShowError] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalData, setTotalData] = useState(0);
  const [fetchedData, setFetchedData] = useState(0);
  const [projectName, setProjectName] = useState(project?.projectName ?? "");
  const [projectType, setProjectType] = useState(project?.projectType ?? "client");
  const [isUpdated, setIsUpdated] = useState(false);
  const dispatch = useDispatch();
  useEffect(() => {
    if (!isCreate && project) {
      setProjectType(project?.projectType || "client");
      setSelectedOptions(
        (project?.assignees || [])
          .map((user) => {
            const userId = Number(user.userId);
            if (!Number.isInteger(userId) || userId <= 0) return null;
            return {
              value: userId,
              label: `${user.firstname} ${user.lastname}`,
              createTaskSheet: Boolean(user.createTaskSheet),
            };
          })
          .filter(Boolean)
      );
    }
  }, []);
  const handleInputChange = (inputValue) => {
    // setPage(1);
    // setOptions([]);
    // fetchOptions(inputValue);
  };
  useEffect(() => {
    if (!isCreate && isDataUpdated()) {
      setIsUpdated(true);
    } else {
      setIsUpdated(false);
    }
  }, [projectName, selectedOptions, projectType]);
  useEffect(() => {
    if ((page === 1 || totalData > fetchedData) && !loading) {
      setLoading(true);
      fetchEmployees(page).then((res) => {
        const final = res?.data;
        if (!final?.pagination || !Array.isArray(final.userData)) {
          setLoading(false);
          return;
        }
        setTotalData(final.pagination.totalData);
        setFetchedData((pre) => pre + final.userData.length);
        const optData = final.userData
          .filter((user) => user.roleId !== 1 && !isCeoOrCmoRoleUser(user))
          .map((user) => ({
            value: Number(user.userId),
            label: `${user.firstname} ${user.lastname}(${user.role})`,
            createTaskSheet: false,
          }));
        if (page === 1) setOptions(optData);
        else {
          setOptions((pre) => [...pre, ...optData]);
        }
        setLoading(false);
      });
    }
  }, [page]);
  const toUserId = (value) => {
    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? id : null;
  };

  const newAddedAssignees = () => {
    const existingAssignees = new Set(
      (project?.assignees || [])
        .map((asgn) => toUserId(asgn.userId))
        .filter(Boolean)
    );
    return selectedOptions
      .map((opt) => ({
        userId: toUserId(opt.value),
        createTaskSheet: Boolean(opt.createTaskSheet),
      }))
      .filter((assignment) => assignment.userId && !existingAssignees.has(assignment.userId));
  };

  const removedAssignees = () => {
    const selectedSet = new Set(
      selectedOptions.map((opt) => toUserId(opt.value)).filter(Boolean)
    );
    return (project?.assignees || [])
      .map((asgn) => toUserId(asgn.userId))
      .filter((userId) => userId && !selectedSet.has(userId));
  };
  const fetchEmployees = async (page) => {
    try {
      const res = await dispatch(getEmployeesAPI({ page, limit: 15 })).unwrap();
      return res;
    } catch (error) {
      console.log("error :>> ", error);
      return null;
    }
  };

  const handleMenuScrollToBottom = () => {
    if (!loading && fetchedData < totalData) {
      setPage((pre) => pre + 1);
    }
  };
  const validateData = () => {
    if (projectName.trim().length) return false;
    else return true;
  };
  const isDataUpdated = () => {
    if (
      projectName.trim() !== project.projectName ||
      projectType !== (project.projectType || "client") ||
      newAddedAssignees()?.length > 0 ||
      removedAssignees()?.length > 0 ||
      sheetSettingsChanged().length > 0
    ) {
      return true;
    }
    return false;
  };
  const sheetSettingsChanged = () => {
    if (isCreate || !project?.assignees?.length) return [];
    const existingAssignees = new Map(
      project.assignees
        .map((asgn) => [toUserId(asgn.userId), Boolean(asgn.createTaskSheet)])
        .filter(([userId]) => userId)
    );
    return selectedOptions
      .map((opt) => ({
        userId: toUserId(opt.value),
        createTaskSheet: Boolean(opt.createTaskSheet),
      }))
      .filter((assignment) => assignment.userId && existingAssignees.has(assignment.userId))
      .filter(
        (assignment) =>
          existingAssignees.get(assignment.userId) !== assignment.createTaskSheet
      );
  };
  const handleAssigneesChange = (selected) => {
    const currentById = new Map(selectedOptions.map((opt) => [opt.value, opt]));
    setSelectedOptions(
      (selected || []).map((opt) => ({
        ...opt,
        createTaskSheet: Boolean(
          currentById.has(opt.value)
            ? currentById.get(opt.value).createTaskSheet
            : opt.createTaskSheet
        ),
      }))
    );
  };
  const updateAssigneeSheetToggle = (userId, checked) => {
    setSelectedOptions((current) =>
      current.map((opt) =>
        opt.value === userId ? { ...opt, createTaskSheet: checked } : opt
      )
    );
  };
  const onSubmit = () => {
    if (!validateData()) {
      if (isCreate) {
        let payload = {
          name: projectName.trim(),
          projectType,
          userAssignments: selectedOptions.map((opt) => ({
            userId: opt.value,
            createTaskSheet: Boolean(opt.createTaskSheet),
          })),
        };
        dispatch(createProjectAPI(payload));
        toggleDetailDrawer();
      } else {
        let payload = {};
        if (isDataUpdated()) {
          const newAssignees = newAddedAssignees();
          const removedAssignee = removedAssignees();
          const changedSheetSettings = sheetSettingsChanged();
          if (projectName.trim() !== project?.projectName)
            payload.name = projectName.trim();
          if (projectType !== (project?.projectType || "client"))
            payload.projectType = projectType;
          if (newAssignees?.length > 0) payload.userAssignments = newAssignees;
          if (changedSheetSettings?.length > 0)
            payload.sheetSettings = changedSheetSettings;
          if (removedAssignee?.length > 0)
            payload.unassignUserIds = removedAssignee;
          dispatch(updateProjectAPI({ id: project?.projectId, payload }));
          toggleDetailDrawer();
        }
      }
      return;
    }
    setShowError(true);
    return;
  };

  return (
    <div>
      <Drawer
        open={isEditOpen}
        onClose={() => toggleDetailDrawer()}
        placement={window.innerWidth > 700 ? "right" : "bottom"}
        className="drawer"
        title={isCreate ? "Add Project" : "Edit Project"}
        width="400px"
      >
        <div className="p-4">
          <div>
            <Input
              label="Project Name"
              value={projectName}
              placeholder="Project Name"
              onChange={(e) => {
                setProjectName(e.target.value);
              }}
            />
            <ErrorText
              message={"Please provide valid project name."}
              show={showError && projectName.trim().length === 0}
            />
          </div>
          <div className="mt-6">
            <label className="text-[#111827] font-semibold">Project Type</label>
            <Select
              className="react-select-container mt-2"
              classNamePrefix="react-select"
              options={PROJECT_TYPE}
              value={PROJECT_TYPE.find((opt) => opt.value === projectType) || PROJECT_TYPE[0]}
              onChange={(option) => setProjectType(option?.value || "client")}
              isClearable={false}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 4000 }),
                menu: (base) => ({ ...base, zIndex: 4000 }),
              }}
            />
            <p className="text-xs text-[#6b7280] mt-2">
              Client projects use free-form tasks in daily work updates. Internal projects pull tasks from the project board.
            </p>
          </div>
          <div className="mt-6 ">
            <label className="text-[#111827] font-semibold">
              Assign Employee
            </label>
            <Select
              isMulti
              className="react-select-container mt-2"
              classNamePrefix="react-select"
              options={options}
              value={selectedOptions}
              onChange={handleAssigneesChange}
              onMenuScrollToBottom={handleMenuScrollToBottom}
              isLoading={loading}
              placeholder="Select Employee..."
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 4000 }),
                menu: (base) => ({ ...base, zIndex: 4000 }),
              }}
            />
            {selectedOptions.length > 0 ? (
              <div className="mt-4 space-y-3">
                {selectedOptions.map((opt) => (
                  <div
                    key={opt.value}
                    className="flex items-center justify-between gap-3 rounded-lg border border-[#e5e7eb] px-3 py-2"
                  >
                    <div>
                      <div className="text-sm font-semibold text-[#111827]">
                        {opt.label}
                      </div>
                      <div className="text-xs text-[#6b7280]">
                        Create Google Sheet for this project
                      </div>
                    </div>
                    <Switch
                      checked={Boolean(opt.createTaskSheet)}
                      onChange={(checked) =>
                        updateAssigneeSheetToggle(opt.value, checked)
                      }
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <div className="mt-6">
            {isCreate || isUpdated ? (
              <button
                onClick={onSubmit}
                className="bg-btn-primary px-4 py-2 text-white float-right cursor-pointer font-semibold rounded-lg"
              >
                {isCreate ? "Create" : "Update"}
              </button>
            ) : null}
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default ProjectDrawer;
